import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Heart, ExternalLink, Gift, Users, ChefHat, Wrench, Check, Smartphone, DollarSign } from "lucide-react";
import SEO from "../components/SEO";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_c2c-nonprofit/artifacts/0drtr76p_c2c.png";
const GOFUNDME_URL = "https://www.gofundme.com/f/confidence-2-create-neeko-feeds-the-community?attribution_id=sl:fc12d34f-d673-4fe6-8f68-3b67a3d4ac79&lang=en_US&ts=1772903959&utm_campaign=fp_sharesheet&utm_content=amp17_ta-amp20_control&utm_medium=customer&utm_source=copy_link";
const CASHAPP_URL = "https://cash.app/$ConfidencetwoCreate";
const ZELLE_QR_URL = "/tmp/zelle_qr.png";

const impactItems = [
  { amount: "$25", description: "Provides supplies for one youth workshop session" },
  { amount: "$50", description: "Feeds 10 seniors through our Meals on Wheels program" },
  { amount: "$100", description: "Sponsors one youth for a full week of skills training" },
  { amount: "$250", description: "Funds a community cooking event for 50+ people" },
  { amount: "$500", description: "Supports a month of youth leadership programming" },
  { amount: "$1000", description: "Sponsors an entire summer cooking camp session" },
];

const donationMethods = [
  {
    title: "Online Donation",
    description: "Quick and secure donation through GoFundMe",
    icon: Heart,
    action: "Donate via GoFundMe",
    href: GOFUNDME_URL,
    primary: true,
  },
  {
    title: "Cash App",
    description: "Send directly via Cash App",
    icon: DollarSign,
    action: "Send via Cash App",
    href: CASHAPP_URL,
    primary: true,
    cashtag: "$ConfidencetwoCreate",
  },
  {
    title: "Zelle",
    description: "Send via Zelle to Confidence2create Inc",
    icon: Smartphone,
    action: "Send via Zelle",
    href: "https://enroll.zellepay.com/qr-codes?data=eyJuYW1lIjoiQ29uZmlkZW5jZTJjcmVhdGUgSW5jIiwiYWN0aW9uIjoicGF5bWVudCIsInRva2VuIjoiNjEyNTU5MTI4NiJ9",
    primary: true,
    zellePhone: "(612) 559-1286",
  },
  {
    title: "Volunteer Your Time",
    description: "Share your skills and expertise with our youth",
    icon: Users,
    action: "Contact Us",
    href: "/contact",
    primary: false,
  },
  {
    title: "Donate Supplies",
    description: "Food, tools, equipment, and materials",
    icon: Gift,
    action: "Contact Us",
    href: "/contact",
    primary: false,
  },
];

const programs = [
  { icon: ChefHat, name: "Culinary Programs", description: "Teaching youth cooking and food service skills" },
  { icon: Wrench, name: "Construction Training", description: "Hands-on building and carpentry skills" },
  { icon: Users, name: "Community Meals", description: "Feeding seniors and families in need" },
];

export default function Donations() {
  return (
    <div data-testid="donations-page" className="pt-20">
      <SEO
        title="Donate"
        description="Support Confidence 2 Create Inc. Your donation helps train youth in valuable skills, feed seniors, and build a stronger Saint Paul. #EverybodyEats"
      />
      {/* Hero Section */}
      <section className="hero-gradient py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://customer-assets.emergentagent.com/job_c2c-nonprofit/artifacts/rq5gatvi_serving%202.jpg" 
            alt="Neeko Feeding Community" 
            className="w-full h-full object-cover object-top"
          />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <span className="inline-block bg-[#FFC107] text-[#0033A0] px-4 py-2 rounded-full text-sm font-bold mb-6">
                Support Our Mission
              </span>
              <h1 className="text-3xl md:text-6xl font-black text-white mb-6">
                Help Us <span className="text-[#FFC107]">Feed</span> the Community
              </h1>
              <p className="text-base md:text-xl text-white/90 mb-6 md:mb-8 leading-relaxed">
                Your donation helps us train youth in valuable skills, feed seniors in our community, and build a stronger Saint Paul. Every dollar makes a difference.
              </p>
              <p className="text-2xl md:text-3xl font-bold text-[#FFC107] italic mb-6 md:mb-8">#Everybody eats</p>
              
              <a
                href={GOFUNDME_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-gofundme-btn"
                className="btn-secondary inline-flex items-center gap-3 text-lg"
              >
                <Heart size={24} />
                Donate Now on GoFundMe
                <ExternalLink size={18} />
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="flex justify-center"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-[#FFC107] rounded-full blur-2xl opacity-30 transform scale-110" />
                <img
                  src={LOGO_URL}
                  alt="C2C Logo"
                  className="relative w-64 h-64 md:w-80 md:h-80 object-contain"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Donation Methods */}
      <section className="section-padding bg-white" data-testid="donation-methods-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#FFC107] font-bold tracking-wide uppercase">Ways to Give</span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0033A0] mt-4">
              How You Can Help
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {donationMethods.map((method, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`p-6 md:p-8 rounded-2xl text-center ${
                  method.primary
                    ? "bg-[#0033A0] text-white"
                    : "bg-slate-50 border border-slate-200"
                }`}
                data-testid={`donation-method-${index}`}
              >
                <div className={`w-16 h-16 mx-auto mb-6 rounded-2xl flex items-center justify-center ${
                  method.primary ? "bg-[#FFC107]" : "bg-[#0033A0]"
                }`}>
                  <method.icon className={`w-8 h-8 ${
                    method.primary ? "text-[#0033A0]" : "text-white"
                  }`} />
                </div>
                <h3 className={`text-xl font-bold mb-3 ${
                  method.primary ? "text-white" : "text-[#0033A0]"
                }`}>
                  {method.title}
                </h3>
                <p className={`mb-4 ${
                  method.primary ? "text-white/80" : "text-slate-600"
                }`}>
                  {method.description}
                </p>
                {method.cashtag && (
                  <p className="text-[#FFC107] font-bold text-lg mb-4">{method.cashtag}</p>
                )}
                {method.zellePhone && (
                  <p className="text-[#FFC107] font-bold text-lg mb-4">{method.zellePhone}</p>
                )}
                {method.href.startsWith("http") ? (
                  <a
                    href={method.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`donation-action-${index}`}
                    className={`inline-flex items-center gap-2 font-bold ${
                      method.primary
                        ? "btn-secondary"
                        : "btn-primary"
                    }`}
                  >
                    {method.action}
                    {method.primary && <ExternalLink size={16} />}
                  </a>
                ) : (
                  <Link
                    to={method.href}
                    data-testid={`donation-action-${index}`}
                    className="btn-primary inline-block"
                  >
                    {method.action}
                  </Link>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="section-padding bg-slate-50" data-testid="impact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#FFC107] font-bold tracking-wide uppercase">Your Impact</span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0033A0] mt-4 mb-4">
              See What Your Donation Does
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Every contribution, no matter the size, makes a real difference in our community.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {impactItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-lg transition-all border border-slate-100 hover:border-[#FFC107] card-hover"
                data-testid={`impact-item-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 bg-[#0033A0] rounded-xl flex items-center justify-center">
                      <span className="text-[#FFC107] font-black text-lg">{item.amount}</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-slate-700 font-medium">{item.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs We Support */}
      <section className="section-padding bg-[#0033A0]" data-testid="programs-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#FFC107] font-bold tracking-wide uppercase">Where Your Money Goes</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-4">
              Programs You Support
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {programs.map((program, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur p-8 rounded-2xl border border-white/20"
                data-testid={`program-support-${index}`}
              >
                <div className="w-14 h-14 bg-[#FFC107] rounded-xl flex items-center justify-center mb-6">
                  <program.icon className="w-7 h-7 text-[#0033A0]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{program.name}</h3>
                <p className="text-white/80">{program.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Donate Section */}
      <section className="section-padding bg-white" data-testid="why-donate-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#FFC107] font-bold tracking-wide uppercase">Why Give?</span>
              <h2 className="text-4xl font-bold text-[#0033A0] mt-4 mb-6">
                Your Support Changes Lives
              </h2>
              <div className="space-y-4">
                {[
                  "100% of donations go directly to programs",
                  "Tax-deductible contributions",
                  "Transparent use of funds",
                  "Local impact in Saint Paul & Twin Cities",
                  "Empowering youth with real skills",
                  "Caring for our community elders",
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-[#FFC107] rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-[#0033A0]" />
                    </div>
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-slate-50 p-8 md:p-12 rounded-2xl"
            >
              <h3 className="text-2xl font-bold text-[#0033A0] mb-6 text-center">
                Ready to Make a Difference?
              </h3>
              <p className="text-slate-600 text-center mb-8">
                Click below to donate securely through GoFundMe and join our mission to empower our community.
              </p>
              <a
                href={GOFUNDME_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="final-gofundme-btn"
                className="btn-primary w-full flex items-center justify-center gap-3"
              >
                <Heart size={20} />
                Donate on GoFundMe
                <ExternalLink size={18} />
              </a>
              <p className="text-center text-slate-500 text-sm mt-4">
                Secure donation powered by GoFundMe
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-[#FFC107]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0033A0] mb-4">
            Have Questions About Donating?
          </h2>
          <p className="text-[#0033A0]/80 text-lg mb-8">
            We're happy to discuss how your donation can make the biggest impact.
          </p>
          <Link
            to="/contact"
            data-testid="donation-contact-btn"
            className="btn-primary inline-block"
          >
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
}
